import React from 'react';
import { useGetMyDraftsQuery, MyDraft } from '../store/topicApi';

const ITTRIDENT_URL = process.env.REACT_APP_ITTRIDENT_URL || 'http://localhost:3001';

const approvalBadge = (status: string) => {
  if (status === 'approved') return <span className="badge-green">✓ Approved</span>;
  if (status === 'rejected') return <span className="badge-red">✗ Rejected</span>;
  return <span className="badge-yellow">⏳ Pending</span>;
};

const DraftCard: React.FC<{ draft: MyDraft }> = ({ draft }) => {
  const previewUrl = draft.userApprovalToken
    ? `${ITTRIDENT_URL}/#/preview-${draft._id}?token=${draft.userApprovalToken}`
    : draft.tempPublishedUrl;

  return (
    <div className="bg-white rounded-2xl border border-trident-gray-100 shadow-card p-5 hover:shadow-card-hover transition-all">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {draft.category && (
              <span className="badge-orange">{draft.category}</span>
            )}
            {approvalBadge(draft.userApprovalStatus)}
            {draft.userEdited && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200">
                ✏️ Edited
              </span>
            )}
          </div>
          <h3 className="font-display font-bold text-base text-trident-gray-900 leading-snug">
            {draft.title}
          </h3>
          {draft.excerpt && (
            <p className="text-sm text-trident-gray-500 line-clamp-2 mt-1">{draft.excerpt}</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-trident-gray-100">
        <span className="text-xs text-trident-gray-500">
          {draft.updatedAt ? `Updated ${new Date(draft.updatedAt).toLocaleDateString()}` : '—'}
        </span>
        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-display font-semibold text-trident-orange hover:text-trident-orange-dark transition-colors"
          >
            {draft.userApprovalStatus === 'pending' ? 'Edit & Approve' : 'View Preview'}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </a>
        )}
      </div>
    </div>
  );
};

const MyDrafts: React.FC = () => {
  const { data: drafts = [], isLoading, error } = useGetMyDraftsQuery();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-trident-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-500 font-semibold">Failed to load drafts.</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display font-bold text-2xl text-trident-gray-900">My Drafts</h2>
        <p className="text-sm text-trident-gray-500">
          Blogs generated from your submissions. Review and approve the preview to publish.
        </p>
      </div>

      {drafts.length === 0 ? (
        <div className="card text-center py-16">
          <div className="w-16 h-16 rounded-full bg-trident-orange-light text-trident-orange flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          </div>
          <h3 className="font-display font-bold text-lg text-trident-gray-900 mb-1">No drafts yet</h3>
          <p className="text-sm text-trident-gray-500">Submitted forms awaiting approval will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {drafts.map((d) => <DraftCard key={d._id} draft={d} />)}
        </div>
      )}
    </div>
  );
};

export default MyDrafts;
